import React from 'react';
import clsx from 'clsx';
import {
  CheckmarkOutline,
  Error,
  Notification,
  Warning,
} from '@danske/sapphire-icons/react';
import styles from '@danske/sapphire-css/components/feedbackMessage/feedbackMessage.module.css';

import { Icon, Typography } from '../..';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import { filterDOMProps } from '@react-aria/utils';

export type FeedbackMessageVariant =
  | 'success'
  | 'error'
  | 'warning'
  | 'informative'
  | 'neutral';

export interface FeedbackMessageProps
  extends GlobalDomAttributes,
    SapphireStyleProps {
  /**
   * Heading content, rendered in a sapphire Heading component.
   */
  heading: React.ReactNode | string;
  /**
   * The type of message.
   * The 'error' and 'success' are meant to attract more attention.
   *
   * @default 'neutral'
   */
  variant?: FeedbackMessageVariant;
  /**
   * Optional icon to override the default icon for the message type.
   */
  icon?: React.ReactNode;
  /**
   * Optional description, rendered as a section below heading.
   */
  body?: React.ReactNode;
  /**
   *  Optional actions, rendered at the bottom. Use {@link ButtonGroup}
   *  for the content.
   */
  actions?: React.ReactNode;
}

const getColor = (variant: FeedbackMessageVariant) => {
  switch (variant) {
    case 'success':
      return 'positive';
    case 'error':
      return 'negative';
    case 'neutral':
      return 'primary';
    default:
      return variant;
  }
};

const getIcon = (variant: FeedbackMessageVariant, icon?: React.ReactNode) => {
  if (icon) {
    return icon;
  }

  switch (variant) {
    case 'success':
      return <CheckmarkOutline />;
    case 'error':
      return <Error />;
    case 'warning':
      return <Warning />;
    case 'informative':
      return <Notification />;
    default:
      return null;
  }
};

/**
 * Implements layout and style of a feedback message.
 */
export const FeedbackMessage = ({
  heading,
  body,
  actions,
  variant = 'neutral',
  icon,
  ...otherProps
}: FeedbackMessageProps): JSX.Element => {
  useThemeCheck();
  const {
    styleProps: { style, className },
  } = useSapphireStyleProps(otherProps);

  const iconElement = getIcon(variant, icon);

  return (
    <div
      className={clsx(styles['sapphire-feedback-message'], className)}
      style={style}
      {...filterDOMProps(otherProps, { global: true })}
    >
      {iconElement !== null && (
        <div className={styles['sapphire-feedback-message__header']}>
          <Icon size="xl" color={getColor(variant)}>
            {iconElement}
          </Icon>
        </div>
      )}
      <div className={styles['sapphire-feedback-message__content']}>
        {typeof heading === 'string' ? (
          <Typography.Body size="lg" isSemibold>
            {heading}
          </Typography.Body>
        ) : (
          heading
        )}
        <Typography.Body elementType="section" size="sm">
          {body}
        </Typography.Body>
      </div>
      {actions && (
        <div className={styles['sapphire-feedback-message__footer']}>
          {actions}
        </div>
      )}
    </div>
  );
};
